/**
 * Slope-class colormap — DOMAIN data-encoding, НЕ Earth Tech theme token.
 *
 * Шкала уклона поля по 4 классам (0–2° / 2–5° / 5–10° / >10°) для баров
 * распределения площади в табе «Рельеф» (AK-207). Зелёный→жёлтый→оранжевый→
 * красный — общепринятая агрономическая шкала «пологое → крутое» (риск
 * эрозии/смыва растёт с уклоном). Это data-encoding, а не UI-тема: при
 * ребрендинге Earth Tech НЕ меняется, стабильность важна для сравнимости
 * между полями/хозяйствами.
 *
 * Whitelisted в `frontend-vite/CLAUDE.md` (hex разрешён в `src/lib/colorMaps/`).
 */

export interface SlopeClass {
  /** Ключ поля в TerrainResponse (slope_class_*_pct). */
  key:
    | 'slope_class_0_2_pct'
    | 'slope_class_2_5_pct'
    | 'slope_class_5_10_pct'
    | 'slope_class_10_plus_pct';
  /** Подпись диапазона для UI. */
  label: string;
  /** Цвет бара (hex, data-encoding). */
  color: string;
}

/** Порядок баров сверху вниз: от пологого к крутому. */
export const SLOPE_CLASSES: readonly SlopeClass[] = [
  { key: 'slope_class_0_2_pct', label: '0–2°', color: '#3faf46' },
  { key: 'slope_class_2_5_pct', label: '2–5°', color: '#e0b823' },
  { key: 'slope_class_5_10_pct', label: '5–10°', color: '#e08a2a' },
  { key: 'slope_class_10_plus_pct', label: '> 10°', color: '#d84a3a' },
] as const;

// ─── AK-211 — слой «Рельеф» (заливка полей по классу уклона) ────────────────
//
// Тот же 4-классовый data-encoding, что и бары AK-207 выше (переиспользуем
// hex), но для choropleth-заливки полей на карте по `slope_mean` (° из MVT
// `fields_styled`). Пороги классов: 0–2 / 2–5 / 5–10 / >10 градусов.

/** Верхние границы классов (°) — общие для легенды и MapLibre step-выражения. */
export const SLOPE_CLASS_BREAKS = [2, 5, 10] as const;

/**
 * Цвет для поля без данных рельефа (slope_mean = NULL: pending/failed/
 * непосчитано). Нейтральный серый — «нет данных», НЕ путается с классами.
 * data-encoding, whitelisted (src/lib/colorMaps/).
 */
export const SLOPE_NO_DATA_COLOR = '#9CA3AF';

/** Строка легенды слоя «Рельеф»: swatch + подпись класса. */
export interface SlopeLegendRow {
  color: string;
  label: string;
}

/**
 * Легенда слоя «Рельеф» — 4 класса + строка «нет данных». Подписи из мокапа
 * AK-211 (вариант 1, утв. founder): равнина/пологий/склон/крутой.
 */
export const SLOPE_LEGEND_ROWS: readonly SlopeLegendRow[] = [
  { color: '#3faf46', label: '0–2° равнина' },
  { color: '#e0b823', label: '2–5° пологий' },
  { color: '#e08a2a', label: '5–10° склон' },
  { color: '#d84a3a', label: '> 10° крутой' },
  { color: SLOPE_NO_DATA_COLOR, label: 'нет данных' },
] as const;

/**
 * MapLibre `fill-color` выражение для слоя «Рельеф»: классификация поля по
 * `slope_mean` в один из 4 цветов, NULL → нейтральный «нет данных».
 *
 * `['get','slope_mean']` возвращает null когда свойства нет (старый кэш-тайл
 * без styled-v2) или terrain_status≠computed — `case`-гвард ставит серый
 * ДО `step` (step с null-input даёт непредсказуемый результат). Пороги =
 * SLOPE_CLASS_BREAKS, цвета = SLOPE_LEGEND_ROWS (единый источник значений).
 */
export function slopeFillColorExpression(): unknown[] {
  const [c0, c1, c2, c3] = SLOPE_LEGEND_ROWS;
  return [
    'case',
    ['==', ['typeof', ['get', 'slope_mean']], 'number'],
    [
      'step',
      ['get', 'slope_mean'],
      c0.color, // < 2°
      SLOPE_CLASS_BREAKS[0],
      c1.color, // 2–5°
      SLOPE_CLASS_BREAKS[1],
      c2.color, // 5–10°
      SLOPE_CLASS_BREAKS[2],
      c3.color, // > 10°
    ],
    SLOPE_NO_DATA_COLOR, // slope_mean отсутствует → нет данных
  ];
}
